import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { SidebarProvider } from "@/components/ui/sidebar";
import { ArrowLeft, ShieldCheck, Zap } from "lucide-react";
import {
  useProject,
  usePipelineStatus,
  useAgentSchema,
  usePreparedInputs,
  useExecutions,
  useExecution,
  useProjectCosts,
} from "@/hooks/useApi";
import { runModelLabel, soleModelLabel } from "@/lib/model-label";
import { formatCount } from "@/lib/format";
import { formatDate } from "@/lib/datetime";
import { useAgentExecution } from "@/hooks/useAgentExecution";
import { AgentInputForm } from "@/components/agents/AgentInputForm";
import { WarningsBanner } from "@/components/agents/WarningsBanner";
import { resolveWarnings } from "@/components/agents/resolveWarnings";
import { AppSidebar } from "@/components/layout/AppSidebar";
import { WorkbenchHeaderBar } from "@/components/agents/functional/WorkbenchHeaderBar";
import { PipelineProgress } from "@/components/agents/functional/PipelineProgress";
import { HeroSection } from "@/components/brand/HeroSection";
import { HeroOverline } from "@/components/brand/HeroOverline";
import { CompactAgentGlyph } from "@/components/brand/CompactAgentGlyph";
import type { PipelineNode, NodeStatus as PipelineNodeStatus } from "@/components/agents/functional/PipelineProgress";
import {
  GRAPH_NAME_TO_NODE_ID,
  NODE_NAME_I18N_CONTEXT,
  NODES,
  completedRunStatuses,
  hasDeliveryOutput,
  resolveOutputs,
  type NodeId,
  type NodeStatus,
} from "@/components/agents/functional/patternC/nodeSpec";
import { HumanInputPanel } from "@/components/agents/functional/patternC/HumanInputPanel";
import { NodeStageCard } from "@/components/agents/functional/patternC/NodeStageCard";
import { NodeData } from "@/components/agents/functional/patternC/renderers";
import { ScopeActivationPanel } from "@/components/agents/functional/ScopeActivationPanel";
import { DigitalAccessCoverageBlock } from "@/components/agents/functional/DigitalAccessCoverageBlock";
import { PricingAppUploadPanel } from "@/components/agents/functional/PricingAppUploadPanel";
import { BomAdjustPanel } from "@/components/agents/functional/BomAdjustPanel";
import {
  OverviewView,
  type OverviewDrillTarget,
} from "@/components/agents/functional/aderencia/OverviewView";
import { WorklistView } from "@/components/agents/functional/aderencia/WorklistView";
import {
  CatalogView,
  type SheetLoadOutcome,
} from "@/components/agents/functional/aderencia/CatalogView";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import type {
  EligibilityVerdict,
  ScopeActivationResponse,
} from "@/api/scope-activation";
import {
  getConfigExtractionStatus,
  getScopeApproval,
  getScopeSummary,
  isReopenBlocked,
} from "@/api/scope-activation";
import { ConsolidatedView } from "@/components/agents/functional/aderencia/ConsolidatedView";
import { GapRegisterView } from "@/components/agents/functional/gaps/GapRegisterView";
import { useProjectGaps } from "@/hooks/useProjectGaps";
import { buildNodeData, tabsWithCounts } from "@/components/agents/functional/patternC/nodeData";
import { ActivityDrawer, type ActivityEvent } from "@/components/agents/functional/patternC/ActivityDrawer";
import { FloatingChat, type ChatMode } from "@/components/agents/functional/patternC/FloatingChat";
import {
  downloadBomFile,
  downloadBomCsv,
  getFunctionalDeliveryStatus,
} from "@/api/agents";
import { toast } from "sonner";

const AGENT_ID = "functional";

/**
 * Fase do SAP Activate a que este agente pertence.
 *
 * Declarada aqui e não lida de lugar nenhum porque **não existe fonte**: nem a
 * definição YAML nem o `/schema` têm campo de fase. O Architect faz igual, com
 * `phaseLabel="Discover"` (#832). Fica como constante nomeada em vez de string
 * solta no JSX, para o dia em que a fase virar dado haver um lugar só para
 * trocar.
 */
const AGENT_PHASE = "Prepare";

export default function FunctionalWorkbench() {
  const { t, i18n } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { data: project } = useProject(projectId);
  usePipelineStatus(projectId); // keep cache warm
  const { data: schema } = useAgentSchema(projectId, AGENT_ID);
  const { data: preparedInputs } = usePreparedInputs(projectId, AGENT_ID);
  // S-018: slim list — apenas metadata (status/timestamps/metrics) das runs
  // anteriores. Payload cai de ~10MB/run pra ~1KB. output_summary da última
  // run completada é buscado sob demanda via useExecution mais abaixo.
  const { data: executions = [] } = useExecutions(projectId, AGENT_ID, {
    slim: true,
  });
  // Só pelo rótulo do modelo: é o fallback de run antiga, que não gravou o dela.
  const { data: costs } = useProjectCosts(projectId);

  const execution = useAgentExecution();

  const [activeNodeId, setActiveNodeId] = useState<NodeId>("extract");
  // Fit-to-Standard: "só disponível no país" vale pras 3 abas (Overview/Decisions/Catalog).
  const [countryOnly, setCountryOnly] = useState(true);
  // Aprovação do escopo: aprovado congela o projeto (Verdict read-only + New Run off).
  const { data: scopeApproval } = useQuery({
    queryKey: ["scope-approval", projectId],
    queryFn: () => getScopeApproval(projectId!),
    enabled: !!projectId,
  });
  const scopeApproved = scopeApproval?.approved ?? false;

  // #854: a entrega roda num worker, fora da sessão. Sem consultar o job, a
  // tela fica muda entre aprovar e o resultado aparecer: na validação ao vivo só
  // mostrava depois de um F5. O polling para sozinho quando ela termina ou
  // morre; manter o intervalo aceso seria bater no banco para sempre.
  const { data: delivery } = useQuery({
    queryKey: ["functional-delivery", projectId],
    queryFn: () => getFunctionalDeliveryStatus(projectId!),
    enabled: !!projectId && scopeApproved,
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s === "pending" || s === "running" ? 4000 : false;
    },
  });
  const deliveryStatus = delivery?.status ?? null;

  // Terminou a entrega: o payload da execução mudou no servidor (ganhou BOM e
  // relatório), e sem rebuscar a tela mostraria os nós de volta em "pendente".
  // É o passo que substitui o F5 que o teste ao vivo exigia.
  const queryClient = useQueryClient();
  useEffect(() => {
    if (deliveryStatus !== "done") return;
    queryClient.invalidateQueries({
      queryKey: ["execution", projectId, AGENT_ID],
    });
    queryClient.invalidateQueries({ queryKey: ["executions", projectId, AGENT_ID] });
  }, [deliveryStatus, projectId, queryClient]);
  // Catraca (gate 3): o Configuration Server já extraiu (ready) em cima
  // deste escopo? Se sim, reabrir trava na tela — o backend já bloqueia com
  // 409, mas expor a trava aqui evita o analista descobrir isso só no clique.
  const { data: configExtractionStatus } = useQuery({
    queryKey: ["config-extraction-status", projectId],
    queryFn: () => getConfigExtractionStatus(projectId!),
    enabled: !!projectId,
  });
  const hasReadyExtraction = isReopenBlocked(configExtractionStatus);
  // Edição pendente do Fit-to-Standard (staged), compartilhada entre Catalog
  // (edita/salva) e Consolidated (aprova só sem pendências).
  const [scopeStaged, setScopeStaged] = useState<
    Record<string, EligibilityVerdict>
  >({});
  // Resultado do upload do CSV do catálogo: fica aqui para sobreviver à troca de aba.
  const [sheetLoadOutcome, setSheetLoadOutcome] = useState<SheetLoadOutcome | null>(null);
  const hasUnsavedScope = Object.keys(scopeStaged).length > 0;
  useEffect(() => {
    if (!hasUnsavedScope) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [hasUnsavedScope]);
  const [activeTabByNode, setActiveTabByNode] = useState<Record<NodeId, string>>(() =>
    NODES.reduce(
      (acc, n) => {
        acc[n.id] = n.defaultTab;
        return acc;
      },
      {} as Record<NodeId, string>,
    ),
  );
  const [drawerPinned, setDrawerPinned] = useState(true);
  const [drawerCollapsed, setDrawerCollapsed] = useState(true);
  const [chatMode, setChatMode] = useState<ChatMode>("closed");
  const [showInputSheet, setShowInputSheet] = useState(false);

  const latestCompleted = executions.find((e) => e.status === "completed");
  // HITL F5-rehydration (S-021 HITL-4.2): se há uma run pausada e nenhuma
  // execução viva ainda no hook, hidrata a partir do detail. Senão a UI
  // perderia o HumanInputPanel ao recarregar a página.
  const pausedExecutionSummary = executions.find((e) => e.status === "paused");
  const isRunning = execution.status === "running";
  const shouldHydratePaused =
    !isRunning &&
    execution.status === "idle" &&
    !!pausedExecutionSummary;
  // S-018: a slim list não traz output_summary. Quando temos latestCompleted
  // e não estamos rodando, buscamos o detail (full state) sob demanda — UMA
  // request a mais (~1-5MB) em vez de 76MB carregados upfront.
  const { data: latestCompletedDetail } = useExecution(
    projectId,
    AGENT_ID,
    isRunning ? null : latestCompleted?.id ?? null,
  );
  // HITL F5: detail do paused vem em parallel — usado pra rehidratar
  // humanInputRequest no hook e pra exibir os outputs parciais (nós já
  // terminados antes da pausa).
  const { data: pausedDetail } = useExecution(
    projectId,
    AGENT_ID,
    shouldHydratePaused ? pausedExecutionSummary?.id ?? null : null,
  );

  useEffect(() => {
    if (pausedDetail && shouldHydratePaused && projectId) {
      execution.hydratePaused(projectId, AGENT_ID, pausedDetail);
    }
    // execution.hydratePaused é estável (useCallback []); pausedDetail muda
    // de referência quando a query React Query retorna.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pausedDetail, shouldHydratePaused, projectId]);
  // S-017: outputs caem pra latestCompletedDetail SÓ quando não há run rodando.
  // Durante run, só mostramos o que a própria execução streamou (que começa
  // null e vai preenchendo via SSE). Evita BOM tab exibir dados da run anterior
  // como se fossem da nova.
  // #854: a entrega grava direto no banco, sem passar pelo stream. O `outputs`
  // que o hook guarda em memória é o do levantamento e nunca ganha o BOM, então
  // depois da entrega quem manda é o persistido. Ver `resolveOutputs`.
  const persistedOutputs =
    ((pausedDetail?.output_summary as Record<string, unknown> | null) ??
      (latestCompletedDetail?.output_summary as
        | Record<string, unknown>
        | null)) ??
    null;
  const outputs = resolveOutputs(
    execution.outputs ?? null,
    persistedOutputs,
    isRunning,
  );
  // #816: avisos com a mesma precedência de `outputs` e do mesmo payload. O
  // único caso em que `resolveOutputs` descarta um stream presente é a entrega
  // feita: aí os avisos do stream pararam na Fase A e os do banco já trazem os
  // dos nós 05 a 07.
  const streamSuperseded =
    execution.outputs != null && outputs !== execution.outputs;
  const warnings = resolveWarnings({
    live: streamSuperseded ? [] : execution.warnings,
    latestCompleted: isRunning ? null : persistedOutputs,
  });
  // S-020: workbench já sabe que uma run completou (latestCompleted existe),
  // mas o detail (~19MB) ainda não chegou. Diferente de "run rodando" ou
  // "nenhuma run". Skeleton nas tabs vazias dá feedback de "carregando" em
  // vez do PlaceholderBanner ("aguardando nó upstream").
  const isLoadingRunDetail = !outputs && !isRunning && !!latestCompleted;
  // S-017: isComplete só é true quando NÃO há run em andamento. Antes, qualquer
  // latestCompleted (run anterior persistida) tornava isComplete=true mesmo
  // durante uma nova run — fazendo nodeStatuses marcar todos os 6 nodes como
  // "done" enquanto extract_ecc_data ainda nem terminou.
  const isComplete =
    !isRunning && (execution.status === "completed" || !!latestCompleted);
  const isFailed = execution.status === "failed";
  // HITL (S-021): run pausada esperando input humano. Estado próprio para
  // o nodeStatuses não interpretar como "completou" ou "rodando".
  const isPaused = execution.status === "paused";
  const humanInputRequest = execution.humanInputRequest;

  // S-021 HITL-3.5/4.2 (F2): quando o agent pausa, salta a tab visível
  // pro nó onde a pausa aconteceu. Sem isso, o HumanInputPanel renderiza
  // dentro do NodeStageCard do nó padrão (map = 03), mesmo que o pause
  // tenha sido em resolve (04) — usuário fica confuso porque o overline
  // do card diz "Map Scope Items" enquanto o payload é de resolve.
  useEffect(() => {
    if (!humanInputRequest) return;
    const targetNodeId = GRAPH_NAME_TO_NODE_ID[humanInputRequest.node_name];
    if (targetNodeId) {
      setActiveNodeId(targetNodeId);
    }
  }, [humanInputRequest]);

  // Enquanto roda, o node "em visualização" acompanha o node ativo — o anel de
  // selecionado segue o run em vez de ficar preso no default (antes, "map" 03
  // aparecia selecionado mesmo com o Extract 01 rodando / estado vazio).
  useEffect(() => {
    if (!isRunning) return;
    const targetNodeId = GRAPH_NAME_TO_NODE_ID[execution.phase];
    if (targetNodeId) {
      setActiveNodeId(targetNodeId);
    }
  }, [isRunning, execution.phase]);

  // Per-node status derivation.
  // O índice da fase vem da posição do nó atual DENTRO de NODES (7 nós), não do
  // PHASE_INDEX global do hook — esse mapa é compartilhado entre agentes e não
  // acompanha os 7 nós daqui: lá `generate_report` é 5, aqui é 6, desde a
  // inserção do assess_activation. Derivar de NODES + o nome da fase mantém o
  // stepper correto pra qualquer mudança de nós.
  // A entrega chegou a rodar nesta execução? Vem do payload persistido, não
  // do stream: é o que faz o estado sobreviver a um F5.
  const deliveryDone = useMemo(() => hasDeliveryOutput(outputs), [outputs]);

  const nodeStatuses: Record<NodeId, NodeStatus> = useMemo(() => {
    const phaseNodeId = GRAPH_NAME_TO_NODE_ID[execution.phase];
    const derivedIdx = phaseNodeId
      ? NODES.findIndex((n) => n.id === phaseNodeId)
      : -1;
    const phaseIdx = derivedIdx >= 0 ? derivedIdx : execution.phaseIndex;
    return NODES.reduce(
      (acc, node, i) => {
        // S-017: isRunning checa PRIMEIRO. Antes, isComplete (que herdava do
        // latestCompleted) ganhava precedência e zerava o caso de running.
        if (isRunning) {
          if (i < phaseIdx) acc[node.id] = "done";
          else if (i === phaseIdx) acc[node.id] = "active";
          else acc[node.id] = "pending";
        } else if (isPaused) {
          // S-021 HITL: nó parado em `interrupt()`. phaseIdx aponta pro
          // último phase event recebido, que é exatamente onde o pause
          // aconteceu.
          if (i < phaseIdx) acc[node.id] = "done";
          else if (i === phaseIdx) acc[node.id] = "paused";
          else acc[node.id] = "pending";
        } else if (isComplete) {
          // #854: run concluída sem entrega é a Fase A — parou no nó 04
          // esperando a decisão de escopo. O estado sai do que ficou gravado
          // na execução, então fechar e reabrir mostra a mesma coisa.
          acc[node.id] = completedRunStatuses(
            deliveryDone,
            scopeApproved,
            deliveryStatus,
          )[node.id];
        } else if (isFailed) {
          // Last received phaseIdx marks where the run died.
          // Earlier nodes did finish; later nodes never started.
          if (i < phaseIdx) acc[node.id] = "done";
          else if (i === phaseIdx) acc[node.id] = "error";
          else acc[node.id] = "pending";
        } else {
          acc[node.id] = "pending";
        }
        return acc;
      },
      {} as Record<NodeId, NodeStatus>,
    );
  }, [
    deliveryDone,
    deliveryStatus,
    scopeApproved,
    execution.phase,
    execution.phaseIndex,
    isComplete,
    isFailed,
    isPaused,
    isRunning,
  ]);

  // Live elapsed timer for the running node
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!isRunning) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [isRunning]);

  const elapsedFor = useCallback(
    (nodeId: NodeId): string => {
      const status = nodeStatuses[nodeId];
      if (status === "pending") return "—";
      if (status === "active" && execution.startedAt != null) {
        return formatDurationMs(Date.now() - execution.startedAt);
      }
      if (status === "done" && execution.metrics?.latency_ms != null) {
        return formatDurationMs(execution.metrics.latency_ms);
      }
      return "—";
    },
    // tick intentionally drives the live re-render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nodeStatuses, execution.startedAt, execution.metrics, tick],
  );

  // Build per-tab data once per outputs change
  const nodeData = useMemo(
    () =>
      buildNodeData({
        outputs: outputs ?? null,
        metrics: (execution.metrics as unknown as Record<string, unknown>) ?? null,
      }),
    // i18n.language força o recálculo quando a conta troca de idioma: os builders
    // chamam i18n.t() por dentro (nodeData.ts não é componente, não pode usar
    // useTranslation), e sem este dep o memo não saberia que precisa recomputar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [outputs, execution.metrics, i18n.language],
  );

  // Pipeline progress nodes (shape expected by PipelineProgress)
  const pipelineNodes: PipelineNode[] = useMemo(() => {
    return NODES.map((n) => {
      const s = nodeStatuses[n.id];
      const status: PipelineNodeStatus =
        s === "done"
          ? "complete"
          : s === "active" || s === "loading"
            ? "running"
            : s === "paused"
              ? "paused"
              : s === "awaiting"
                ? "awaiting"
              : s === "error"
                ? "error"
                : "queued";
      const tag =
        s === "loading"
          ? t("Loading…")
          : status === "running"
          ? t("Now running")
          : status === "complete"
            ? t("Node {{num}}", { num: n.num })
            : status === "error"
              ? t("Errored")
              : status === "awaiting"
                ? t("Your decision")
                : // Aprovado: a entrega saiu (ou vai sair) noutra execução, e
                  // esta aqui nunca vai rodar estes nós. "Pending" não afirma
                  // nada; "Awaits approval" afirmaria algo já resolvido.
                  deliveryDone || scopeApproved
                  ? t("Pending")
                  : t("Awaits approval");
      return {
        id: n.id,
        shortName: t(n.name, { context: NODE_NAME_I18N_CONTEXT }),
        status,
        tag,
        meta: elapsedFor(n.id),
        tabsCount: n.tabs.length,
      };
    });
    // i18n.language força o recálculo do shortName traduzido quando a conta troca
    // de idioma — t sozinho não muda de referência a cada troca.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeStatuses, elapsedFor, deliveryDone, scopeApproved, t, i18n.language]);

  // Nó 04: aderência Fit-to-Standard. O nó roda automaticamente como parte
  // do pipeline e grava `scope_activation` (payload de to_response) no run.
  // Lemos do run, não do botão manual — o painel manual segue como fallback
  // de "re-rodar" quando o run não trouxe dados (sem conexão SAP / falha).
  const scopeActivation = useMemo(() => {
    const sa = outputs?.scope_activation as
      | ScopeActivationResponse
      | undefined
      | null;
    if (sa && Array.isArray(sa.items) && sa.summary) return sa;
    return null;
  }, [outputs]);

  // Execution do Fit-to-Standard (run atual → senão a última completada).
  const scopeExecutionId =
    execution.executionId || latestCompleted?.id || undefined;

  // O resultado do upload é de um projeto e de uma run: a rota não tem `key`
  // por projeto, então sem isto o diálogo de uma run apareceria na outra.
  useEffect(() => {
    setSheetLoadOutcome(null);
  }, [projectId, scopeExecutionId]);

  // Agregado server-side pelo veredito EFETIVO (analista → senão sugestão):
  // alimenta o Overview e os contadores das abas, refletindo edições + aprovação.
  const { data: scopeSummary } = useQuery({
    queryKey: [
      "scope-summary",
      projectId,
      scopeExecutionId,
      countryOnly,
      scopeApproval?.version ?? null,
    ],
    queryFn: () =>
      getScopeSummary(projectId!, {
        executionId: scopeExecutionId!,
        countryOnly,
      }),
    enabled: !!projectId && !!scopeExecutionId && !!scopeActivation,
    placeholderData: (prev) => prev,
  });

  const activeNode = NODES.find((n) => n.id === activeNodeId) ?? NODES[0];
  const activeTab = activeTabByNode[activeNode.id] ?? activeNode.defaultTab;
  // O Registro de GAPs não vem da run: a contagem é a da lista do projeto.
  const { data: gapList } = useProjectGaps(
    activeNode.id === "activation" ? projectId : undefined,
  );
  const activeTabsWithCounts = useMemo(
    () =>
      tabsWithCounts(activeNode, outputs, countryOnly, scopeSummary).map((tab) =>
        tab.id === "gaps" && gapList ? { ...tab, count: gapList.totals.gaps } : tab,
      ),
    [activeNode, outputs, countryOnly, scopeSummary, gapList],
  );
  const activePayload = nodeData[`${activeNode.id}.${activeTab}`];

  // S-012: só libera download quando a run está REALMENTE persistida no DB.
  // `execution.executionId` é setado quando a run inicia, não quando termina —
  // antes do _persist_execution, o endpoint do agents retorna 404 ("Execution
  // not found"). Durante isRunning, escondemos completamente (BOM tab mostra
  // a run nova via streaming, mas a anterior persistida seria o que viria no
  // download — UX confusa). Fora de run: execução recém-completed > latestCompleted.
  // Botão é renderizado condicionalmente via `data.bom_file_path && onDownload`
  // nos componentes, então `null` aqui = botão escondido.
  const downloadableExecutionId = isRunning
    ? null
    : execution.status === "completed"
      ? execution.executionId
      : latestCompleted?.id ?? null;
  const bomFilePath =
    (outputs?.bom_summary as { bom_file_path?: unknown } | undefined)
      ?.bom_file_path;
  const [downloadingBom, setDownloadingBom] = useState(false);
  const handleDownloadBom = useCallback(async () => {
    if (!projectId || !downloadableExecutionId) return;
    setDownloadingBom(true);
    try {
      await downloadBomFile(projectId, AGENT_ID, downloadableExecutionId);
      toast.success(t("BOM downloaded."));
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : t("Failed to download BOM"),
      );
    } finally {
      setDownloadingBom(false);
    }
  }, [projectId, downloadableExecutionId]);

  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const handleDownloadCsv = useCallback(async () => {
    if (!projectId || !downloadableExecutionId) return;
    setDownloadingCsv(true);
    try {
      await downloadBomCsv(projectId, AGENT_ID, downloadableExecutionId);
      toast.success(t("BOM (CSV) downloaded."));
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : t("Failed to download BOM CSV"),
      );
    } finally {
      setDownloadingCsv(false);
    }
  }, [projectId, downloadableExecutionId]);

  const handleRun = useCallback(
    (formData: Record<string, unknown>) => {
      if (!projectId) return;
      setShowInputSheet(false);
      execution.execute(projectId, AGENT_ID, {
        message: "Run Functional analysis",
        context: formData,
      });
    },
    [projectId, execution],
  );

  const handleTabChange = useCallback(
    (tabId: string) => {
      setActiveTabByNode((prev) => ({ ...prev, [activeNode.id]: tabId }));
    },
    [activeNode.id],
  );

  // Drill-down dos totalizadores da Visão Geral do Fit-to-Standard: abre o
  // Catálogo já filtrado pelo que o card conta. O "Precisa da sua atenção"
  // conta o veredito efetivo (analista, senão sugestão), e por isso não vai
  // para a aba Decisões, que lista a sugestão crua da IA. O `seq` remonta o
  // Catálogo com o filtro novo a cada clique, inclusive quando ele já estava
  // aberto.
  const [catalogPreset, setCatalogPreset] = useState<{
    seq: number;
    verdict?: string;
    confidence?: string;
    cleanCore?: boolean;
  }>({ seq: 0 });
  const handleOverviewDrill = useCallback(
    (target: OverviewDrillTarget) => {
      setCatalogPreset((prev) => ({
        seq: prev.seq + 1,
        verdict: target === "decisions" ? "decision" : undefined,
        confidence: target === "low_confidence" ? "baixa" : undefined,
        cleanCore: target === "clean_core" ? true : undefined,
      }));
      handleTabChange("catalog");
    },
    [handleTabChange],
  );
  // Troca de aba pela barra limpa o filtro do drill-down: o Catálogo aberto
  // pela aba mostra a lista inteira.
  const handleTabBarChange = useCallback(
    (tabId: string) => {
      setCatalogPreset((prev) =>
        prev.verdict || prev.confidence || prev.cleanCore
          ? { seq: prev.seq + 1 }
          : prev,
      );
      handleTabChange(tabId);
    },
    [handleTabChange],
  );

  const onNodePick = useCallback((nextId: string) => {
    setActiveNodeId(nextId as NodeId);
  }, []);

  // Hero meta values
  const runId =
    execution.executionId?.slice(0, 5) ?? latestCompleted?.id.slice(0, 5) ?? "—";
  const runState = isRunning
    ? "RUNNING"
    : isPaused
      ? "PAUSED"
      : isFailed
        ? "FAILED"
        : isComplete
          ? "COMPLETED"
          : "IDLE";
  const heroElapsed = isRunning && execution.startedAt
    ? formatDurationMs(Date.now() - execution.startedAt)
    : execution.metrics?.latency_ms != null
      ? formatDurationMs(execution.metrics.latency_ms)
      : "0:00";
  // `execution.metrics` só é preenchido pelo evento `complete` do SSE, então
  // abrir o workbench do zero o deixa nulo — e o `runId` logo acima já usa a
  // última run completada. Sem a queda para `latestCompleted`, o hero
  // identificava uma run antiga e lhe atribuía o modelo configurado hoje. A
  // lista slim passa pelo proxy que rotula, então esse `metrics` já vem pronto.
  // O último degrau é o único modelo medido do projeto, e só quando há **um**:
  // com dois, não existe resposta honesta para uma run que não gravou o dela.
  // Antes era a configuração da api, que não executa run nenhuma.
  const heroModel = runModelLabel(
    execution.metrics ?? latestCompleted?.metrics,
    soleModelLabel(costs?.models),
  );
  const currentNodeName = (() => {
    const running = NODES.find((n) => nodeStatuses[n.id] === "active");
    if (running) return t(running.name, { context: NODE_NAME_I18N_CONTEXT });
    const errored = NODES.find((n) => nodeStatuses[n.id] === "error");
    if (errored)
      return t("Error · {{name}}", {
        name: t(errored.name, { context: NODE_NAME_I18N_CONTEXT }),
      });
    if (isComplete) return t("Ready");
    return t("Standby");
  })();

  // Stage summary subtitle (e.g. "126 scope items · 85% complete")
  const stageSummary = stageSummaryText(activeNode.id, outputs, t);

  // Drawer events come from the live execution; use displayedExecutionId as fallback ref
  const drawerEvents: ActivityEvent[] = execution.activity;

  // When chat is fullscreen, collapse the drawer for focus
  const effectiveDrawerCollapsed = chatMode === "fullscreen" ? true : drawerCollapsed;

  const status = nodeStatuses[activeNode.id];

  return (
    <SidebarProvider defaultOpen={false}>
      <div className="flex flex-col h-screen w-full overflow-hidden bg-background">
        <WorkbenchHeaderBar
          projectId={projectId!}
          projectName={project?.name || t("Project")}
          runCount={executions.length}
        />

        <div className="flex flex-1 overflow-hidden min-h-0">
          <AppSidebar />

          <main className="flex-1 min-w-0 flex flex-col overflow-auto relative">
            <HeroSection padding="compact" className="shrink-0">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-[11px] text-white/70 hover:text-white hover:bg-white/10 gap-1 -ml-2 mb-2"
                onClick={() => navigate(`/projects/${projectId}`)}
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                {t("Back to Project")}
              </Button>
              <div className="flex justify-between items-start gap-6 flex-wrap">
                <div className="flex items-start gap-3.5">
                  {/* Os dois pontos acendem só com execução rodando, como no
                      `AgentPageLayout` e no `ConfigurationServer` (#1042). O
                      glifo é o compartilhado: a cópia local que ficou aqui
                      depois da extração tinha o ponto sempre aceso. */}
                  <CompactAgentGlyph number={1} live={isRunning} />
                  <div>
                    <HeroOverline live={isRunning} className="mb-1.5">
                      {t("AGENT {{num}} OF {{total}}", { num: "01", total: "07" })} ·{" "}
                      {t(runState)}
                      {runId !== "—" && ` · ${t("RUN #{{id}}", { id: runId })}`}
                    </HeroOverline>
                    <h1 className="font-brand font-extrabold text-[26px] leading-[1.05] tracking-[-0.025em] text-white m-0 mb-2 flex items-center gap-2.5 flex-wrap">
                      Functional
                      <span
                        className="font-mono text-[9px] font-semibold px-2 py-0.5 rounded-full border border-white/15 text-white/80 uppercase tracking-[0.08em]"
                        style={{ background: "rgba(255,255,255,0.08)" }}
                      >
                        {t("License Assessment")}
                      </span>
                    </h1>
                    <div className="flex items-center gap-3 flex-wrap text-[11px] text-white/55 font-mono tracking-[0.04em]">
                      <span>
                        <span className="text-white/40 mr-1">{t("Phase")}</span>
                        <strong className="text-white/90 font-medium">
                          {AGENT_PHASE}
                        </strong>
                      </span>
                      <span className="text-white/25">·</span>
                      <span>
                        <span className="text-white/40 mr-1">{t("Pipeline")}</span>
                        {/* #832: vinha escrito "6 nodes" enquanto a faixa logo
                            abaixo renderizava 7, na mesma dobra. Conta de
                            `NODES`, que é a fonte que a faixa consulta. */}
                        <strong className="text-white/90 font-medium">
                          {t("{{count}} nodes", { count: formatCount(NODES.length) })}
                        </strong>
                      </span>
                      <span className="text-white/25">·</span>
                      <span>
                        <span className="text-white/40 mr-1">{t("Current")}</span>
                        <strong className="text-white/90 font-medium">{currentNodeName}</strong>
                      </span>
                      <span className="text-white/25">·</span>
                      <span>
                        <span className="text-white/40 mr-1">{t("Elapsed")}</span>
                        <strong className="text-white/90 font-medium">{heroElapsed}</strong>
                      </span>
                      {heroModel && (
                        <>
                          <span className="text-white/25">·</span>
                          <span>
                            <span className="text-white/40 mr-1">{t("Model")}</span>
                            <strong className="text-white/90 font-medium">{heroModel}</strong>
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex gap-1.5 items-center">
                  {/* Sem botão "Chat" aqui (#1044): a pílula do FloatingChat,
                      no canto, abre o mesmo painel e é a única entrada. */}
                  {/* title no span (não no button): tooltip não aparece em
                      <button disabled>, mas o span recebe o hover. */}
                  <span
                    title={
                      scopeApproved
                        ? t(
                            "Functional scope approved — duplicate the project to run a new Functional analysis",
                          )
                        : undefined
                    }
                  >
                    <Button
                      type="button"
                      variant="action"
                      className="h-[30px] gap-1.5 px-3 text-[11px] font-semibold"
                      disabled={scopeApproved}
                      onClick={() => setShowInputSheet(true)}
                    >
                      <Zap className="w-3 h-3" /> {isComplete ? t("New Run") : t("Continue")}
                    </Button>
                  </span>
                </div>
              </div>
            </HeroSection>

            {/* Com pausa HITL o banner vai para baixo do painel de decisão,
                que é o que pede ação agora. */}
            {!humanInputRequest && (
              <WarningsBanner warnings={warnings} className="m-3.5 shrink-0" />
            )}

            <div className="sticky top-0 z-20">
              <PipelineProgress
                nodes={pipelineNodes}
                activeNode={activeNodeId}
                onNodeClick={onNodePick}
              />
            </div>

            <div className="p-3.5 flex bg-[#f7f7f7] shrink-0">
              <NodeStageCard
                node={activeNode}
                status={status}
                elapsed={elapsedFor(activeNode.id)}
                summary={stageSummary}
                tabs={activeTabsWithCounts}
                activeTab={activeTab}
                onTabChange={handleTabBarChange}
                onCsv={
                  activeNode.id === "bom" &&
                  bomFilePath &&
                  downloadableExecutionId
                    ? handleDownloadCsv
                    : undefined
                }
                downloadingCsv={downloadingCsv}
                onDownloadXlsx={
                  activeNode.id === "bom" &&
                  bomFilePath &&
                  downloadableExecutionId
                    ? handleDownloadBom
                    : undefined
                }
                downloadingXlsx={downloadingBom}
              >
                {humanInputRequest && (
                  <>
                    <HumanInputPanel
                      request={humanInputRequest}
                      onResume={execution.resume}
                    />
                    <WarningsBanner warnings={warnings} className="mb-4" />
                  </>
                )}
                {activeNode.id === "activation" ? (
                  scopeActivation && scopeActivation.summary.total > 0 ? (
                    <>
                      <div className="mb-3 flex items-center justify-between gap-2">
                        {scopeApproved ? (
                          <span className="inline-flex items-center gap-1.5 rounded bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-700 ring-1 ring-emerald-200">
                            <ShieldCheck className="h-3.5 w-3.5" />
                            {t("Functional scope approved · v{{version}}", {
                              version: scopeApproval?.version,
                            })}
                            {scopeApproval?.approved_at
                              ? ` · ${formatDate(scopeApproval.approved_at)}`
                              : ""}
                          </span>
                        ) : (
                          <span className="text-[11px] text-muted-foreground">
                            {t("Draft — Functional scope editable")}
                          </span>
                        )}
                        <label className="inline-flex cursor-pointer items-center gap-2 text-[11px] text-muted-foreground">
                          <input
                            type="checkbox"
                            className="h-3.5 w-3.5 accent-emerald-600"
                            checked={countryOnly}
                            onChange={(e) => setCountryOnly(e.target.checked)}
                          />
                          {t("Country-available only")}
                        </label>
                      </div>
                      {activeTab === "decisions" ? (
                        <WorklistView
                          data={scopeActivation}
                          countryOnly={countryOnly}
                        />
                      ) : activeTab === "catalog" ? (
                        <CatalogView
                          key={catalogPreset.seq}
                          initialVerdict={catalogPreset.verdict}
                          initialConfidence={catalogPreset.confidence}
                          initialCleanCore={catalogPreset.cleanCore}
                          data={scopeActivation}
                          projectId={projectId}
                          executionId={
                            execution.executionId ||
                            latestCompleted?.id ||
                            undefined
                          }
                          countryOnly={countryOnly}
                          approved={scopeApproved}
                          staged={scopeStaged}
                          setStaged={setScopeStaged}
                          loadOutcome={sheetLoadOutcome}
                          setLoadOutcome={setSheetLoadOutcome}
                        />
                      ) : activeTab === "gaps" && projectId ? (
                        <GapRegisterView projectId={projectId} />
                      ) : activeTab === "consolidated" ? (
                        <ConsolidatedView
                          projectId={projectId}
                          executionId={
                            execution.executionId ||
                            latestCompleted?.id ||
                            undefined
                          }
                          approved={scopeApproved}
                          hasUnsaved={hasUnsavedScope}
                          approvalVersion={scopeApproval?.version}
                          approvedAt={scopeApproval?.approved_at}
                          hasReadyExtraction={hasReadyExtraction}
                        />
                      ) : (
                        <OverviewView
                          data={scopeActivation}
                          summary={scopeSummary}
                          countryOnly={countryOnly}
                          onDrill={handleOverviewDrill}
                        />
                      )}
                    </>
                  ) : isRunning ? (
                    <div className="text-xs text-muted-foreground">
                      {t(
                        "Fit-to-Standard assessment in progress. The result appears when the run finishes.",
                      )}
                    </div>
                  ) : isLoadingRunDetail ? (
                    <div className="text-xs text-muted-foreground">
                      {t("Loading the run's Fit-to-Standard...")}
                    </div>
                  ) : (
                    <div className="flex flex-col gap-3">
                      <div className="text-xs text-muted-foreground">
                        {t(
                          "Didn't run for this run — no SAP connection or the assessment failed. You can run it manually below.",
                        )}
                      </div>
                      <ScopeActivationPanel projectId={projectId!} />
                    </div>
                  )
                ) : (
                  <>
                    {/* #1345: a BOM do Pricing App entra pelo nó da BOM, depois de ele concluir. */}
                    {activeNode.id === "bom" && status === "done" && projectId && (
                      <>
                        <PricingAppUploadPanel projectId={projectId} />
                        {/* #1480: o ajuste da BOM saiu do Ato 5 e mora aqui, longe do cliente. */}
                        <BomAdjustPanel />
                        <DigitalAccessCoverageBlock projectId={projectId} />
                      </>
                    )}
                    <NodeData data={activePayload} loading={isLoadingRunDetail} />
                  </>
                )}
              </NodeStageCard>
            </div>

            <ActivityDrawer
              events={drawerEvents}
              pinned={drawerPinned}
              setPinned={setDrawerPinned}
              collapsed={effectiveDrawerCollapsed}
              setCollapsed={setDrawerCollapsed}
              elapsed={heroElapsed}
            />

            <FloatingChat
              mode={chatMode}
              setMode={setChatMode}
              projectId={projectId!}
              agentId={AGENT_ID}
              agentLabel="Functional"
              executionId={execution.executionId || latestCompleted?.id || undefined}
              isComplete={isComplete}
            />
          </main>
        </div>

        <Sheet open={showInputSheet} onOpenChange={setShowInputSheet}>
          <SheetContent className="sm:max-w-lg overflow-y-auto">
            <SheetHeader>
              <SheetTitle>{t("Run Functional")}</SheetTitle>
            </SheetHeader>
            <div className="mt-4">
              <AgentInputForm
                schema={schema?.inputs || []}
                schemaLoaded={!!schema}
                preparedInputs={preparedInputs?.inputs || {}}
                preparedSources={preparedInputs?.sources || []}
                agentId={AGENT_ID}
                isRunning={isRunning}
                isComplete={isComplete}
                isLocked={false}
                onRun={handleRun}
                onCancel={() => execution.cancel()}
                projectId={projectId}
              />
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </SidebarProvider>
  );
}

function formatDurationMs(ms: number): string {
  const totalSec = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function stageSummaryText(
  nodeId: NodeId,
  outputs: Record<string, unknown> | null | undefined,
  t: (key: string, options?: Record<string, unknown>) => string,
): string | undefined {
  if (!outputs) return undefined;
  const get = (k: string) => outputs[k] as Record<string, unknown> | undefined;
  switch (nodeId) {
    case "extract": {
      const e = get("extraction_summary");
      if (!e) return undefined;
      const users = e.total_users as number | undefined;
      const inactive = e.total_inactive as number | undefined;
      if (users == null) return undefined;
      return inactive
        ? t("{{users}} users · {{inactive}} inactive", {
            users: formatCount(users),
            inactive: formatCount(inactive),
          })
        : t("{{users}} users", { users: formatCount(users) });
    }
    case "classify": {
      const c = get("classification_summary");
      if (!c) return undefined;
      const active = c.total_active as number | undefined;
      const modules = (c.modules_in_scope as unknown[] | undefined)?.length;
      if (active == null && modules == null) return undefined;
      return [
        active != null ? t("{{count}} active", { count: formatCount(active) }) : null,
        modules != null ? t("{{count}} modules", { count: formatCount(modules) }) : null,
      ]
        .filter(Boolean)
        .join(" · ");
    }
    case "map": {
      const sm = get("scope_mapping");
      if (!sm) return undefined;
      const items = (sm.scope_items as unknown[] | undefined)?.length;
      const unmapped = (sm.unmapped_modules as unknown[] | undefined)?.length;
      if (items == null) return undefined;
      return unmapped
        ? t("{{items}} scope items · {{unmapped}} unmapped", {
            items: formatCount(items),
            unmapped: formatCount(unmapped),
          })
        : t("{{items}} scope items", { items: formatCount(items) });
    }
    case "resolve": {
      const r = get("sku_resolution");
      if (!r) return undefined;
      const mapped = r.total_mapped as number | undefined;
      const skus = (r.bom_line_items as unknown[] | undefined)?.length;
      if (mapped == null) return undefined;
      return skus
        ? t("{{mapped}} users mapped · {{skus}} SKU lines", {
            mapped: formatCount(mapped),
            skus: formatCount(skus),
          })
        : t("{{mapped}} users mapped", { mapped: formatCount(mapped) });
    }
    case "bom": {
      const b = get("bom_summary");
      if (!b) return undefined;
      const annual = b.annual_grand_total as number | undefined;
      if (annual == null) return undefined;
      const compact =
        annual >= 1_000_000
          ? `R$ ${formatCount(annual / 1_000_000, { decimals: 1 })}M`
          : `R$ ${formatCount(annual)}`;
      return t("Annual {{amount}}", { amount: compact });
    }
    case "report": {
      return outputs.mapping_report
        ? t("Markdown via Bedrock Claude · FSM closed")
        : undefined;
    }
    default:
      return undefined;
  }
}
